import { VideoContext } from "@/modules/common/video-player/player.provider";
import VideoPlayer from "@/modules/common/video-player/videoPlayer";
import styled from "@emotion/styled";
import { useTranslation } from "next-i18next";
import { useContext, useEffect, useRef, useState } from "react";
import { useInView } from "react-intersection-observer";
import { EVENT, send } from "@/lib/amplitude";
import { ClientLogger } from "@/scripts/log/client";
import DubbedProfile from "./DubbedProfile";
import DubbedScript from "./DubbedScript";
import DubbedSocial from "./DubbedSocial";

const DubbedVideo = ({
  dubbed,
  setShowPopup,
  isPopupView,
  locale,
  isIos,
  handleNextSwipe,
  isFirst,
  setIsFirst,
  itemSeq,
}: {
  dubbed: any;
  setShowPopup: any;
  isPopupView: boolean;
  locale: any;
  isIos: boolean;
  handleNextSwipe: () => void;
  isFirst: any;
  setIsFirst: any;
  itemSeq: number;
}) => {
  const {
    code,
    users,
    together_flag,
    video,
    file_url,
    like_count,
    comment_count,
    share_count,
    scriptData,
    idx,
    is_mine,
  } = dubbed;

  const {
    playDubbed,
    pause,
    playing,
    stop,
    ref: playerRef,
  } = useContext(VideoContext);
  const [activeNumber, setActiveNumber] = useState(0);
  const [isClick, setIsClick] = useState(false);
  const [ref, inView] = useInView();
  const clickRef = useRef(false);
  const { t } = useTranslation();

  useEffect(() => {
    if (playerRef.current.plyr) {
      const { plyr } = playerRef.current;
      const callback = () => {
        if (!playing) {
          handlePlaying();
        }
      };
      plyr.on("playing", callback);
      return () => {
        plyr.off("playing", callback);
      };
    }
  }, [playerRef.current && playerRef.current.plyr]);

  useEffect(() => {
    if (inView) {
      if (!isIos && !isFirst) {
        handleClick();
      }

      if (isPopupView) {
        setShowPopup(true);
      }

      send(EVENT.VIEW__DUB_VIDEO, {
        screen: "Dubbed",
        dubbed_idx: idx,
        video_idx: video?.idx,
        is_mine,
        item_seq: itemSeq,
      });
    }

    if (!inView) {
      stop();
    }
  }, [inView]);

  useEffect(() => {
    if (!playing) {
      if (serverTimeRef.current) {
        const start_date = serverTimeRef.current;
        serverTimeRef.current = "";

        const requestData = JSON.stringify({
          start_date,
          video_dubbed_idx: idx,
        });
        navigator.sendBeacon("/v1/data/watch/history", requestData);
      }
    }
  }, [playing]);
  const serverTimeRef = useRef("");
  const getServerTime = async () => {
    try {
      const response = await fetch(`/v1/data/time`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      });
      if (!response.ok) {
        return;
      }

      const result = await response.json();
      const { code: result_code, data } = result;
      if (result_code === 200) {
        serverTimeRef.current = data.date;
      }
    } catch (error) {
      ClientLogger.error("getServerTime - DubbedVideo", error);
    }
  };

  const handlePlaying = () => {
    send(EVENT.PLAY__DUB_VIDEO, {
      screen: "Dubbed",
      dubbed_idx: idx,
      video_idx: video?.idx,
      is_mine,
      item_seq: itemSeq,
    });
    getServerTime();

    playDubbed({
      url: file_url,
      handler: (videoTime: number) => {
        if (videoTime === 0) {
          setActiveNumber(0);
          return;
        }
        for (let i = 0; i < scriptData.length; i++) {
          if (
            scriptData[i].start_point - 0.3 <= videoTime &&
            scriptData[i].end_point - 0.3 > videoTime
          ) {
            setActiveNumber(scriptData[i].script_num);
            return;
          }
        }
      },
    });
  };

  const handlePause = () => {
    pause();
  };

  useEffect(() => {
    if (!playing) {
      if (
        clickRef.current &&
        activeNumber === scriptData[scriptData.length - 1].script_num
      ) {
        handleNextSwipe();
        clickRef.current = false;
      }
    }
  }, [playing, activeNumber]);

  const handleClick = () => {
    if (isFirst) {
      setIsFirst(false);
    }
    clickRef.current = !playing;

    if (playing) {
      handlePause();
    } else {
      handlePlaying();
    }
  };

  useEffect(() => {
    if (!isFirst) {
      setIsClick(true);
      setTimeout(() => {
        setIsClick(false);
      }, 500);
    }
  }, [playing]);

  return (
    <Section>
      <Dimm onClick={handleClick} active={isClick} isFirst={isFirst} />
      <ButtonWrapper active={isClick} isFirst={isFirst}>
        {isFirst && (
          <>
            <Tooltip>{t(`tip_dubbed_1st_play`)}</Tooltip>
            <PlayingBtn className="icon icon-player_play" />
          </>
        )}
        {!isFirst &&
          (playing ? (
            <PlayingBtn className="icon icon-player_play" />
          ) : (
            <PlayingBtn className="icon icon-player_pause" />
          ))}
      </ButtonWrapper>
      <DubbedProfile
        idx={idx}
        itemSeq={itemSeq}
        users={users}
        together_flag={together_flag}
        setShowPopup={setShowPopup}
        activeNumber={activeNumber}
        scriptData={scriptData}
        handlePause={handlePause}
      />
      <VideoPlayer />
      <DubbedScript activeNumber={activeNumber} scriptData={scriptData} />
      <DubbedSocial
        idx={idx}
        is_mine={is_mine}
        video={video}
        itemSeq={itemSeq}
        code={code}
        like_count={like_count}
        comment_count={comment_count}
        share_count={share_count}
        handlePause={handlePause}
        locale={locale}
      />
      <DubbedTitle ref={ref}>
        <div className="icon icon-dubbing_face" />
        <AnimationTitle>
          <VideoTitle>{video.title}</VideoTitle>
        </AnimationTitle>
      </DubbedTitle>
    </Section>
  );
};

const Section = styled.section`
  width: 100%;
  height: 100%;
  position: relative;
  background-color: black;
  overflow: hidden;
`;

const Dimm = styled.div<{ active: boolean; isFirst: boolean }>`
  position: absolute;
  width: 100%;
  height: 100%;
  z-index: 2;
  background-color: black;
  ${({ isFirst }) => (isFirst ? `opacity: 0.3;` : `opacity: 0;`)}

  @keyframes Dimmfade {
    from {
      opacity: 0.3;
    }
    to {
      opacity: 0;
    }
  }

  ${({ active }) => active && `animation: Dimmfade 0.5s forwards;`}
`;

const Tooltip = styled.div`
  background: url("/img/tooltip_yellow.png") no-repeat;
  background-size: cover;
  width: 155px;
  height: 40px;
  position: absolute;
  z-index: 3;
  transform: translateY(-140%);
  padding: 8px;
  color: white;
  font-size: var(--fs-c1);
  line-height: var(--lh-c1);
  font-weight: 600;
  text-align: center;
  @keyframes bounce {
    0% {
      transform: translateY(-140%);
    }

    20% {
      transform: translateY(-150%);
    }

    100% {
      transform: translateY(-140%);
    }
  }

  animation: bounce 1s infinite;
`;

const ButtonWrapper = styled.div<{ active: boolean; isFirst: boolean }>`
  background: rgba(255, 255, 255, 0.4);
  width: 60px;
  height: 60px;
  border-radius: 9999px;
  display: flex;
  justify-content: center;
  align-items: center;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 2;

  pointer-events: none;

  ${({ isFirst }) => (isFirst ? `opacity: 1;` : `opacity: 0;`)}

  @keyframes Buttonfade {
    from {
      opacity: 1;
    }
    to {
      opacity: 0;
    }
  }

  ${({ active }) => active && `animation: Buttonfade 0.5s forwards;`}
`;

const PlayingBtn = styled.div`
  color: white;
  font-size: 24px;
`;

const DubbedTitle = styled.div`
  width: 211px;
  height: 34px;
  display: flex;
  border-radius: 8px;
  position: absolute;
  bottom: 40px;
  left: 20px;
  padding: 8px;
  background-color: var(--gray090);
  color: white;

  .icon {
    font-size: 16px;
    margin-right: 4px;
  }
`;

const AnimationTitle = styled.div`
  width: 175px;
  overflow: hidden;
`;

const VideoTitle = styled.div`
  white-space: nowrap;
  font-size: var(--fs-c1);
  line-height: var(--lh-c1);
  font-weight: 400;
  animation: slide 9s linear infinite;

  @keyframes slide {
    to {
      transform: translateX(-200px);
      filter: hue-rotate(0);
    }
    from {
      transform: translateX(175px);
      filter: hue-rotate(360deg);
    }
  }
`;

export default DubbedVideo;
